
import { useState, useCallback } from 'react';

interface PaginationOptions {
  initialPage?: number;
  initialPageSize?: number;
  pageSizeOptions?: number[];
}

interface PaginationResult {
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];
  offset: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  canNextPage: (totalItems: number) => boolean;
  canPrevPage: () => boolean;
  pageCount: (totalItems: number) => number;
  getVisiblePages: (totalItems: number) => number[];
}

/**
 * Hook for managing pagination state
 */
export const usePagination = ({
  initialPage = 1,
  initialPageSize = 10,
  pageSizeOptions = [5, 10, 20, 50, 100]
}: PaginationOptions = {}): PaginationResult => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset to page 1 when page size changes
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  // Calculate the offset for data fetching
  const offset = (currentPage - 1) * pageSize;

  // Calculate the total number of pages
  const pageCount = useCallback((totalItems: number) => {
    return Math.ceil(totalItems / pageSize);
  }, [pageSize]);

  // Check if can go to next page
  const canNextPage = useCallback((totalItems: number) => {
    return currentPage < pageCount(totalItems);
  }, [currentPage, pageCount]);

  // Check if can go to previous page
  const canPrevPage = useCallback(() => {
    return currentPage > 1;
  }, [currentPage]);

  // Go to next page
  const nextPage = useCallback(() => {
    setCurrentPage(prev => prev + 1);
  }, []);

  // Go to previous page
  const prevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  }, []);

  // Get array of visible page numbers
  const getVisiblePages = useCallback((totalItems: number) => {
    const total = pageCount(totalItems);
    const MAX_VISIBLE = 5;
    
    if (total <= MAX_VISIBLE) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    
    // Always show first and last page
    const visiblePages = [1];
    
    // Calculate middle pages
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(total - 1, currentPage + 1);
    
    // Adjust range if we're at the start or end
    if (currentPage <= 2) {
      endPage = Math.min(total - 1, MAX_VISIBLE - 1);
    } else if (currentPage >= total - 1) {
      startPage = Math.max(2, total - MAX_VISIBLE + 2);
    }
    
    // Add ellipsis indicator if needed
    if (startPage > 2) {
      visiblePages.push(-1); // Use negative number to indicate ellipsis
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      visiblePages.push(i);
    }
    
    // Add ellipsis indicator if needed
    if (endPage < total - 1) {
      visiblePages.push(-2); // Use different negative number for second ellipsis
    }
    
    // Add last page
    if (total > 1) {
      visiblePages.push(total);
    }
    
    return visiblePages;
  }, [currentPage, pageCount]);

  return {
    currentPage,
    pageSize,
    pageSizeOptions,
    offset,
    setPage: setCurrentPage,
    setPageSize: handlePageSizeChange,
    nextPage,
    prevPage,
    canNextPage,
    canPrevPage,
    pageCount,
    getVisiblePages,
  };
};
